import type { ApiClient } from '../client';
import {
  parseConstructorV2,
  type ConstructorValue,
} from '../../../features/design-constructor';
import { normalizeTemplate, type Template } from '../../theme';

interface RawResponse {
  template?: unknown;
  design_system?: unknown;
}

export interface DesignSystemResult {
  template: Template;
  designSystem: ConstructorValue | null;
}

/**
 * Per-operator theme fetched once at iframe init from
 * GET /api/game_platform/iframe/v1/design_system. The backend now returns
 * v2-only shape ({ version: 2, palette, overrides, … }) plus a top-level
 * `template` string. Soft-fails: any non-OK / non-JSON response returns
 * `{ template: 'classic', designSystem: null }` so the iframe still boots
 * with Figma defaults.
 */
export async function getDesignSystem(client: ApiClient): Promise<DesignSystemResult> {
  try {
    // Forward `?template=` from the iframe's own URL so staging environments
    // can serve the correct template variant without relying on Referer
    // headers. The production backend ignores unknown query params, so this
    // is safe in every environment.
    const iframeTemplate =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('template')
        : null;
    const path = iframeTemplate
      ? `/loot_machine/api/game_platform/iframe/v1/design_system?template=${encodeURIComponent(iframeTemplate)}`
      : '/loot_machine/api/game_platform/iframe/v1/design_system';
    const raw = await client.request<RawResponse>(path, { method: 'GET' });
    return {
      template: normalizeTemplate(raw?.template),
      designSystem: parseConstructorV2(raw?.design_system),
    };
  } catch {
    return { template: normalizeTemplate(null), designSystem: null };
  }
}
